import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Save, Star, Receipt, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../../lib/api';

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const API_URL = getApiUrl();

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/settings`, { credentials: 'include' });
        if (res.ok) setSettings(await res.json());
        else toast.error('Erreur lors du chargement des paramètres');
      } catch {
        toast.error('Erreur réseau');
      } finally {
        setLoading(false);
      }
    })();
  }, [API_URL]);

  const update = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      google_enabled: !!settings.google_enabled,
      google_url: settings.google_url || '',
      google_rating: settings.google_rating === '' || settings.google_rating == null ? null : Number(settings.google_rating),
      google_review_count: settings.google_review_count === '' || settings.google_review_count == null ? null : Number(settings.google_review_count),
      google_label: settings.google_label || 'Avis Google vérifiés',
      tva_enabled: !!settings.tva_enabled,
      tva_regime: settings.tva_regime || 'assujetti',
      tva_rates: settings.tva_rates || '',
      tva_text: settings.tva_text || '',
      season_enabled: !!settings.season_enabled,
      season_message: settings.season_message || '',
      season_cta_label: settings.season_cta_label || '',
      season_cta_href: settings.season_cta_href || '',
    };
    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSettings(await res.json());
        toast.success('Paramètres enregistrés');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="text-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600 mx-auto mb-3" />
        <p className="text-slate-600">Chargement…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-settings">
      {/* Google Reviews */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-500" /> Avis Google
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-700">Afficher le bloc d'avis Google</p>
              <p className="text-xs text-slate-500">Affiche note et nombre d'avis réels sur la page d'accueil.</p>
            </div>
            <Switch
              checked={settings.google_enabled}
              onCheckedChange={(v) => update({ google_enabled: v })}
              data-testid="google-enabled-switch"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1 block">Lien de votre fiche Google</label>
            <Input
              value={settings.google_url || ''}
              onChange={(e) => update({ google_url: e.target.value })}
              placeholder="https://g.page/... ou https://maps.google.com/..."
              data-testid="google-url-input"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1 block">Note (sur 5)</label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={settings.google_rating ?? ''}
                onChange={(e) => update({ google_rating: e.target.value })}
                placeholder="Ex. 4.9"
                data-testid="google-rating-input"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1 block">Nombre d'avis</label>
              <Input
                type="number"
                min="0"
                value={settings.google_review_count ?? ''}
                onChange={(e) => update({ google_review_count: e.target.value })}
                placeholder="Ex. 37"
                data-testid="google-count-input"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1 block">Libellé</label>
            <Input
              value={settings.google_label || ''}
              onChange={(e) => update({ google_label: e.target.value })}
              placeholder="Avis Google vérifiés"
              data-testid="google-label-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* TVA */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-emerald-600" /> Ligne TVA
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-700">Afficher la mention TVA</p>
              <p className="text-xs text-slate-500">Affichée en pied de page du site.</p>
            </div>
            <Switch
              checked={settings.tva_enabled}
              onCheckedChange={(v) => update({ tva_enabled: v })}
              data-testid="tva-enabled-switch"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1 block">Régime TVA</label>
            <Select value={settings.tva_regime} onValueChange={(v) => update({ tva_regime: v })}>
              <SelectTrigger data-testid="tva-regime-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="assujetti">Assujettie à la TVA</SelectItem>
                <SelectItem value="franchise">Franchise en base (art. 293 B du CGI)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {settings.tva_regime === 'assujetti' && (
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1 block">Taux applicables (optionnel)</label>
              <Input
                value={settings.tva_rates || ''}
                onChange={(e) => update({ tva_rates: e.target.value })}
                placeholder="Ex. 10 % / 20 %"
                data-testid="tva-rates-input"
              />
            </div>
          )}
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1 block">Texte personnalisé (optionnel)</label>
            <Textarea
              value={settings.tva_text || ''}
              onChange={(e) => update({ tva_text: e.target.value })}
              rows={2}
              placeholder="Laisser vide pour utiliser le texte automatique selon le régime."
              data-testid="tva-text-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Saison / Vitrine */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-cyan-600" /> Mode saison
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-700">Afficher le bandeau saison</p>
              <p className="text-xs text-slate-500">Bandeau mis en avant en haut de l'accueil (ex. isolation avant l'hiver).</p>
            </div>
            <Switch
              checked={settings.season_enabled}
              onCheckedChange={(v) => update({ season_enabled: v })}
              data-testid="season-enabled-switch"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1 block">Message</label>
            <Textarea
              value={settings.season_message || ''}
              onChange={(e) => update({ season_message: e.target.value })}
              rows={2}
              placeholder="Ex. Préparez l'hiver : isolation & rénovation énergétique — profitez d'une étude gratuite."
              data-testid="season-message-input"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1 block">Libellé du bouton (optionnel)</label>
              <Input
                value={settings.season_cta_label || ''}
                onChange={(e) => update({ season_cta_label: e.target.value })}
                placeholder="Ex. Demander un devis"
                data-testid="season-cta-label-input"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1 block">Lien du bouton (optionnel)</label>
              <Input
                value={settings.season_cta_href || ''}
                onChange={(e) => update({ season_cta_href: e.target.value })}
                placeholder="Ex. /#contact ou /renovation-energetique"
                data-testid="season-cta-href-input"
              />
            </div>
          </div>
          <p className="text-xs text-slate-500">Laissez le lien vide pour pointer vers le formulaire de contact.</p>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-settings-btn"
          className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Enregistrer les paramètres
        </Button>
      </div>
    </div>
  );
};

export default AdminSettings;
